import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Title, Meta } from '@angular/platform-browser';

import { UserService } from './shared/services/user.service';
import { ClientService } from './shared/services/client.service';
import { UserBookingService } from './shared/services/user-booking.service';
import { SalonService } from './shared/services/salon.service';
import { StylistService } from './shared/services/stylist.service';
import { Booking } from './shared/models/booking.model';
import { Client } from './shared/models/client.model';
import { DialogsService } from './shared/services/dialogs/dialogs.service';
import { MatSidenav } from "@angular/material/sidenav";
import { environment } from '../environments/environment';
import { FooterService } from './shared/services/footer.service';
import { ChangeDetectorRef } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from './shared/services/language.service';

declare var window: any;
declare var FB: any;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent implements OnInit {

  @ViewChild('sideNav', { static: false }) sideNav: MatSidenav;

  public booking: Booking;

  public isLoading: boolean;
  public showHome: number = 0;
  public isBookingEnabled: boolean = false;
  public showFooter: boolean = true;

  public allowSignUp: boolean = true;

  public salon;

  public showCookieBanner: boolean = false;
  private readonly COOKIE_CONSENT_KEY = 'cookieConsentTimestamp';
  private readonly CONSENT_VALIDITY_MONTHS = 6;

  public constructor(public userService: UserService,
    private clientService: ClientService,
    public userBookingService: UserBookingService,
    public router: Router,
    private salonService: SalonService,
    private stylistService: StylistService,
    private dialogsService: DialogsService,
    private footerService: FooterService,
    private changeDetectorRef: ChangeDetectorRef,
    private translateService: TranslateService,
    private languageService: LanguageService,
    private titleService: Title,
    private metaService: Meta,
  ) {
    this.booking = this.userBookingService.getBooking();
    this.isLoading = true;
    this.userBookingService.getBookingChangeRef().subscribe((booking: Booking) => {
      if (booking) {
        this.booking = booking;
      }
    });
    this.footerService.showFooter$.subscribe((show) => {
      this.showFooter = show;
    });

    this.userService.getSalonInitMonitor().subscribe((init: boolean) => {
      if (init) {
        this.isLoading = false;
        let salon = this.userService.getSalon();
        this.salon = salon;
        this.isBookingEnabled = this.userService.getSalon().getEnableOnlineBooking();
        this.userService.setIsBookingEnabled(salon.getEnableOnlineBooking())
        this.userService.setIsReadonly(salon.isReadOnly());
        this.userService.setIsAccountDeleted(salon.getAccountStatus() == 4);
        this.changeDetectorRef.detectChanges();
        if (salon.getShowHome()) {
          this.showHome = salon.getShowHome();
          // TODO: get allowSignUp value
        }
        
        this.languageService.initialize(salon.getDefaultLanguage());

        this.updateSalonMetadata(salon);

        this.initializeSalonAnalytics();
      }
    });


    // facebook async
    // This function initializes the FB variable 
    window.fbAsyncInit = function () {
      FB.init({
        appId: environment.FACEBOOK_APP_ID,
        cookie: true,
        xfbml: true,
        version: 'v17.0'
      });

      FB.AppEvents.logPageView();

    };

    (function (d, s, id) {
      var js, fjs = d.getElementsByTagName(s)[0];
      if (d.getElementById(id)) { return; }
      js = d.createElement(s); js.id = id;
      js.src = "https://connect.facebook.net/en_US/sdk.js";
      if (fjs && fjs.parentNode) {
        fjs.parentNode.insertBefore(js, fjs);
      }
    }(document, 'script', 'facebook-jssdk'));

  }

  ngAfterViewInit() {

  }

  ngOnInit() {
    this.checkCookieConsent();
    this.validateStoredSession();
  }

  /**
   * The session is restored from localStorage without checking the server,
   * so a token the API no longer accepts would leave the user looking
   * logged in while every authenticated request fails. Probe /clients/me
   * once at startup; on a 401 the SessionExpiredInterceptor clears the
   * dead session so the user can log in again.
   */
  private validateStoredSession(): void {
    if (!this.userService.isLoggedIn()) {
      return;
    }

    this.clientService.getClientInfo().subscribe(
      (client: Client) => {
        // Token is valid — refresh the locally cached user details.
        this.userService.setUser(client);
      },
      () => {
        // 401s are handled globally by SessionExpiredInterceptor.
      }
    );
  }

  /**
   * Checks if we need to show the cookie consent banner by comparing
   * the stored timestamp with the current time
   */
  private checkCookieConsent(): void {
    try {
      const consentTimestamp = localStorage.getItem(this.COOKIE_CONSENT_KEY);
      
      if (!consentTimestamp) {
        this.showCookieBanner = true;
        return;
      }

      const lastConsent = new Date(parseInt(consentTimestamp));
      const now = new Date();
      const monthsDiff = (now.getFullYear() - lastConsent.getFullYear()) * 12 + 
                        (now.getMonth() - lastConsent.getMonth());

      this.showCookieBanner = monthsDiff >= this.CONSENT_VALIDITY_MONTHS;
    } catch (e) {
      // If localStorage is disabled or any other error occurs,
      // show the banner to be safe
      this.showCookieBanner = true;
    }
  }

  /**
   * Handles the user accepting the cookie consent
   */
  public acceptCookies(): void {
    try {
      localStorage.setItem(this.COOKIE_CONSENT_KEY, Date.now().toString());
    } catch (e) {
      // Handle localStorage errors silently
      console.warn('Failed to save cookie consent:', e);
    }
    this.showCookieBanner = false;
  }

  public checkLoginState() {
    // console.log('facebook login state');
  }

  public statusChangeCallback(response) {

  }

  public newBooking() {
    if (this.booking && this.booking.getServices().length > 0) {
      this.dialogsService.confirm(
        this.translateService.instant('DIALOG.NEW_BOOKING_TITLE'),
        this.translateService.instant('DIALOG.NEW_BOOKING_MESSAGE')
      )
        .subscribe(
          (confirm) => {
            if (confirm) {
              this.resetPage();
              this.router.navigate(['/bookings']);
            }
          }
        );
    } else {
      this.resetPage();
      this.router.navigate(['/bookings']);
    }
  }

  public viewClientBookings() {
    if (this.sideNav) {
      this.sideNav.close();
    }

    this.router.navigate(['/client/bookings']);
  }

  public viewClientProfile() {
    if (this.sideNav) {
      this.sideNav.close();
    }

    this.router.navigate(['/client/profile']);
  }

  public viewHelpPage() {

    if (this.sideNav) {
      this.sideNav.close();
    }

    this.router.navigate(['/help']);
  }

  public viewHomePage() {
    if (this.sideNav) {
      this.sideNav.close();
    }
    this.router.navigate(['/']);
  }

  public viewContactPage() {

    if (this.sideNav) {
      this.sideNav.close();
    }

    this.router.navigate(['/contact']);
  }

  public resetPage() {
    this.userBookingService.resetBooking();
    this.router.navigate(['/']);
  }

  public onLogin() {
    if (this.userService.isLoggedIn()) {
      this.router.navigate(['/bookings']);
    } else {
      let allowSignUp = true;
      let allowFBLogin = true;
      // get data for all stylists
      this.stylistService.getStylists(this.userService.getSalon().getId()).subscribe(
        (stylists) => {
          // loop through stylists and check if any have allowSignup == 0
          stylists.forEach((individualStylist) => {
            // console.log('individual stylist',individualStylist);
            if (individualStylist.getLetClientsRegister() == 0) {
              //set to false if any stylist doesn't allowSignup
              allowSignUp = false;
            } else if (individualStylist.getLetClientsUseFBLogin() == 0) {
              //set to false if any stylist doesn't FBLogin
              allowFBLogin = false;
            }
          }
          );
          this.dialogsService.login(allowSignUp, allowFBLogin).subscribe((client: Client) => {
            if (client) {
              this.router.navigate(['/bookings']);
            }
          });
        });
    }
  }
  public isStylistsPage(): boolean {
    console.log(this.router.url);
    return this.router.url.includes('/stylists');
  }

  public onLogOut() {
    this.userService.logout();
    this.router.navigate(['/']);
  }

  private updateSalonMetadata(salon): void {
    const clean = (value: any): string => (value == null ? '' : String(value).trim());

    const name = clean(salon.getName());
    if (!name) {
      return;
    }

    const city = clean(salon.getCity());
    const province = clean(salon.getProvince());
    const location = [city, province].filter((part) => !!part).join(', ');

    const title = location
      ? `${name} - Online Booking | ${location}`
      : `${name} - Online Booking`;
    this.titleService.setTitle(title);

    const description = location
      ? `Book your next appointment online at ${name} in ${location}.`
      : `Book your next appointment online at ${name}.`;

    const pageUrl = typeof window !== 'undefined' && window.location ? window.location.href : '';
    const logoUrl = clean(salon.getLogoUrl());

    const tags: { name?: string; property?: string; content: string }[] = [
      { name: 'description', content: description },
      { name: 'robots', content: 'index, follow' },
      { property: 'og:site_name', content: name },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:type', content: 'website' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
    ];

    if (pageUrl) {
      tags.push({ property: 'og:url', content: pageUrl });
    }
    if (logoUrl) {
      tags.push({ property: 'og:image', content: logoUrl });
      tags.push({ name: 'twitter:image', content: logoUrl });
      tags.push({ name: 'twitter:card', content: 'summary_large_image' });
    } else {
      tags.push({ name: 'twitter:card', content: 'summary' });
    }

    tags.forEach((tag) => {
      const selector = tag.name ? `name="${tag.name}"` : `property="${tag.property}"`;
      if (this.metaService.getTag(selector)) {
        this.metaService.updateTag(tag, selector);
      } else {
        this.metaService.addTag(tag);
      }
    });

    this.updateCanonicalLink(pageUrl);
    this.injectSalonStructuredData(salon, description, pageUrl);
  }

  private updateCanonicalLink(pageUrl: string): void {
    if (!pageUrl || typeof document === 'undefined') {
      return;
    }
    let link: HTMLLinkElement = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', pageUrl);
  }

  private injectSalonStructuredData(salon, description: string, pageUrl: string): void {
    if (typeof document === 'undefined') {
      return;
    }

    const clean = (value: any): string => (value == null ? '' : String(value).trim());
    const setIf = (target: any, key: string, value: string) => {
      if (value) {
        target[key] = value;
      }
    };

    const data: any = {
      '@context': 'https://schema.org',
      '@type': 'BeautySalon',
      name: clean(salon.getName()),
    };

    setIf(data, 'description', description);
    setIf(data, 'url', pageUrl);
    setIf(data, 'image', clean(salon.getLogoUrl()));
    setIf(data, 'telephone', clean(salon.getPhone()));

    const address: any = { '@type': 'PostalAddress' };
    const street = [salon.getAddress1(), salon.getAddress2()]
      .map(clean)
      .filter((p) => !!p)
      .join(', ');
    setIf(address, 'streetAddress', street);
    setIf(address, 'addressLocality', clean(salon.getCity()));
    setIf(address, 'addressRegion', clean(salon.getProvince()));
    setIf(address, 'postalCode', clean(salon.getPostal()));
    setIf(address, 'addressCountry', salon.getCountry() ? clean(salon.getCountry().getCountryName()) : '');
    if (Object.keys(address).length > 1) {
      data.address = address;
    }

    const sameAs = [
      salon.getWebsite(),
      salon.getFacebookLink(),
      salon.getInstagramLink(),
      salon.getTwitterLink(),
      salon.getYelpLink(),
      salon.getPinterestLink(),
      salon.getTumblrLink(),
    ]
      .map(clean)
      .filter((link) => !!link);
    if (sameAs.length) {
      data.sameAs = sameAs;
    }

    const existing = document.getElementById('salon-structured-data');
    if (existing) {
      existing.remove();
    }
    const script = document.createElement('script');
    script.id = 'salon-structured-data';
    script.type = 'application/ld+json';
    script.text = JSON.stringify(data);
    document.head.appendChild(script);
  }

  /**
   * Initializes salon analytics if the salon has analytics configured
   */
  private initializeSalonAnalytics(): void {
    if (this.salon && this.salon.getAnalytics()) {
      const analyticsCode = this.salon.getAnalytics();
      // We have to inject the analytics code into the head of the document
      // because the script tag is not executed when it is added to the body
      // and we need to ensure that the script is executed when the page is loaded
    
      // Create a temporary container to parse the HTML
      const tempContainer = document.createElement('div');
      tempContainer.innerHTML = analyticsCode;
      
      // Find all script tags and recreate them to ensure they execute
      const scriptElements = tempContainer.querySelectorAll('script');
      scriptElements.forEach((oldScriptEl: HTMLScriptElement) => {
        const newScriptEl = document.createElement('script');
        
        // Copy all attributes from the old script element
        Array.from(oldScriptEl.attributes).forEach(attr => {
          newScriptEl.setAttribute(attr.name, attr.value);
        });
        
        // Copy the script content
        if (oldScriptEl.innerHTML) {
          const scriptText = document.createTextNode(oldScriptEl.innerHTML);
          newScriptEl.appendChild(scriptText);
        }
        
        // Insert the new script directly into the head
        document.head.appendChild(newScriptEl);
      });
      
      // Insert any non-script elements (like noscript tags) directly
      const nonScriptElements = Array.from(tempContainer.children).filter(el => el.tagName !== 'SCRIPT');
      nonScriptElements.forEach(element => {
        document.head.appendChild(element.cloneNode(true));
      });
    }
  }

}
